/**
 * Revenue Analysis - Board Level Premium Design
 * Institutional + Premium + Fast to Read + Decision-Oriented
 * De Beers-inspired visual authority system
 */

import React, { useState } from 'react';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Target, AlertTriangle,
  CheckCircle, ArrowRight, Users, Globe, Gem, DollarSign,
  ArrowUpRight, ArrowDownRight, Minus, Info, BarChart3, FileText
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine, BarChart, LabelList
} from 'recharts';
import { mockData } from '../../data/mockData';
import RevenueWorldMap from '../../components/RevenueWorldMap';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// Helper function to format quarter label based on language
const formatQuarter = (quarter, language) => {
  if (language === 'pt') {
    return quarter.replace('Q', 'T');
  }
  return quarter;
};

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  // Text
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
  },
  // Backgrounds
  bg: {
    page: '#F5F7FB',
    card: '#FFFFFF',
    dark: '#0D1538',
    header: '#0D1538', // alias for backwards compatibility
  },
  // Borders
  border: {
    default: '#E3E8F1',
    soft: '#EEF2F7',
  },
  // Performance
  status: {
    positive: '#0C8A4B',
    positiveBg: '#EAF7EF',
    negative: '#C33D52',
    negativeBg: '#FBECEF',
    warning: '#A07A18',
    warningBg: '#F8F1DD',
  },
  // Chart
  chart: {
    primary: '#0D1538',
    secondary: '#7A859D',
    budget: '#7A859D',
    prior: '#B8C1D1',
    positive: '#0C8A4B',
    negative: '#C33D52',
  },
  // Accent
  accent: {
    gold: '#B89A3C',
  }
};

// Typography classes
const typography = {
  pageTitle: 'font-serif text-[38px] leading-[44px] font-semibold tracking-[-0.3px]',
  pageSubtitle: 'font-sans text-[15px] leading-[22px] font-normal',
  sectionTitle: 'font-serif text-[24px] leading-[30px] font-semibold tracking-[-0.2px]',
  cardLabel: 'font-sans text-[12px] leading-[16px] font-medium tracking-[0.2px]',
  kpiValue: 'font-sans text-[42px] leading-[44px] font-semibold tracking-[-0.8px]',
  kpiUnit: 'font-sans text-[16px] leading-[20px] font-normal',
  bodyText: 'font-sans text-[16px] leading-[26px] font-normal',
  smallText: 'font-sans text-[13px] leading-[18px] font-normal',
};

// ============ HELPER FUNCTIONS ============
const formatValue = (value, decimals = 0) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return decimals > 0 ? num.toFixed(decimals) : Math.round(num);
};

// Alias for backwards compatibility
const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return Math.round(num);
};

const getVarianceColor = (variance, inverse = false) => {
  const isPositive = inverse ? variance < 0 : variance > 0;
  return isPositive ? colors.status.positive : colors.status.negative;
};

const getVarianceBg = (variance, inverse = false) => {
  const isPositive = inverse ? variance < 0 : variance > 0;
  return isPositive ? colors.status.positiveBg : colors.status.negativeBg;
};

const RevenueAnalysis = ({ year, language, yearlyData, prevYearData, setActiveSection, flowMap }) => {
  const [bridgeView, setBridgeView] = useState('yoy'); // 'yoy' or 'budget'
  const [tableView, setTableView] = useState('metric'); // 'metric' or 'stonetype'
  const [hoveredType, setHoveredType] = useState(null); // For diamond type hover popup
  const [stoneTypeView, setStoneTypeView] = useState('yoy'); // 'yoy' or 'budget' for stone type comparison
  const [quarterlyView, setQuarterlyView] = useState('yoy'); // 'yoy' or 'budget' for quarterly chart
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? 'vs Orç. 1T' : 'vs Orç.';
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';
  
  // ============ GEOGRAPHY-CLIENT INTERACTION STATES ============
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [hoveredClient, setHoveredClient] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  // Client-Country mapping
  const clientCountryMapping = {
    'Client A': 'belgium',
    'Client B': 'uae',
    'Client C': 'israel',
    'Client D': 'india',
    'Client E': 'belgium',
  };
  
  // Country names for display
  const countryNames = {
    belgium: { pt: 'Bélgica', en: 'Belgium' },
    uae: { pt: 'EAU', en: 'UAE' },
    israel: { pt: 'Israel', en: 'Israel' },
    india: { pt: 'Índia', en: 'India' },
  };
  
  // Country flags component using flagcdn.com
  const CountryFlag = ({ countryCode, size = 20 }) => {
    const flagCodes = {
      belgium: 'be',
      uae: 'ae',
      israel: 'il',
      india: 'in',
    };
    const code = flagCodes[countryCode];
    if (!code) return null;
    return (
      <img 
        src={`https://flagcdn.com/w40/${code}.png`}
        srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
        alt={countryNames[countryCode]?.en || countryCode}
        width={size}
        height={Math.round(size * 0.75)}
        style={{ 
          borderRadius: '2px',
          objectFit: 'cover',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}
      />
    );
  };
  
  // Monthly revenue data for each client (2024 vs 2025)
  const clientMonthlyData = {
    'Client A': [
      { month: 'Jan', y2024: 2.1, y2025: 2.4 },
      { month: 'Fev', y2024: 1.9, y2025: 2.2 },
      { month: 'Mar', y2024: 2.3, y2025: 2.8 },
      { month: 'Abr', y2024: 2.0, y2025: 2.3 },
      { month: 'Mai', y2024: 2.2, y2025: 2.6 },
      { month: 'Jun', y2024: 1.8, y2025: 2.1 },
      { month: 'Jul', y2024: 2.4, y2025: 2.9 },
      { month: 'Ago', y2024: 2.1, y2025: 2.5 },
      { month: 'Set', y2024: 2.5, y2025: 3.0 },
      { month: 'Out', y2024: 2.2, y2025: 2.7 },
      { month: 'Nov', y2024: 2.0, y2025: 2.4 },
      { month: 'Dez', y2024: 2.3, y2025: 2.8 },
    ],
    'Client B': [
      { month: 'Jan', y2024: 1.5, y2025: 1.8 },
      { month: 'Fev', y2024: 1.3, y2025: 1.6 },
      { month: 'Mar', y2024: 1.7, y2025: 2.0 },
      { month: 'Abr', y2024: 1.4, y2025: 1.7 },
      { month: 'Mai', y2024: 1.6, y2025: 1.9 },
      { month: 'Jun', y2024: 1.2, y2025: 1.5 },
      { month: 'Jul', y2024: 1.8, y2025: 2.1 },
      { month: 'Ago', y2024: 1.5, y2025: 1.8 },
      { month: 'Set', y2024: 1.9, y2025: 2.2 },
      { month: 'Out', y2024: 1.6, y2025: 1.9 },
      { month: 'Nov', y2024: 1.4, y2025: 1.7 },
      { month: 'Dez', y2024: 1.7, y2025: 2.0 },
    ],
    'Client C': [
      { month: 'Jan', y2024: 1.2, y2025: 1.4 },
      { month: 'Fev', y2024: 1.0, y2025: 1.2 },
      { month: 'Mar', y2024: 1.4, y2025: 1.7 },
      { month: 'Abr', y2024: 1.1, y2025: 1.3 },
      { month: 'Mai', y2024: 1.3, y2025: 1.5 },
      { month: 'Jun', y2024: 0.9, y2025: 1.1 },
      { month: 'Jul', y2024: 1.5, y2025: 1.8 },
      { month: 'Ago', y2024: 1.2, y2025: 1.4 },
      { month: 'Set', y2024: 1.6, y2025: 1.9 },
      { month: 'Out', y2024: 1.3, y2025: 1.5 },
      { month: 'Nov', y2024: 1.1, y2025: 1.3 },
      { month: 'Dez', y2024: 1.4, y2025: 1.6 },
    ],
    'Client D': [
      { month: 'Jan', y2024: 0.9, y2025: 1.1 },
      { month: 'Fev', y2024: 0.8, y2025: 1.0 },
      { month: 'Mar', y2024: 1.1, y2025: 1.3 },
      { month: 'Abr', y2024: 0.9, y2025: 1.1 },
      { month: 'Mai', y2024: 1.0, y2025: 1.2 },
      { month: 'Jun', y2024: 0.7, y2025: 0.9 },
      { month: 'Jul', y2024: 1.2, y2025: 1.4 },
      { month: 'Ago', y2024: 0.9, y2025: 1.1 },
      { month: 'Set', y2024: 1.3, y2025: 1.5 },
      { month: 'Out', y2024: 1.0, y2025: 1.2 },
      { month: 'Nov', y2024: 0.8, y2025: 1.0 },
      { month: 'Dez', y2024: 1.1, y2025: 1.3 },
    ],
    'Client E': [
      { month: 'Jan', y2024: 0.7, y2025: 0.9 },
      { month: 'Fev', y2024: 0.6, y2025: 0.8 },
      { month: 'Mar', y2024: 0.8, y2025: 1.0 },
      { month: 'Abr', y2024: 0.7, y2025: 0.9 },
      { month: 'Mai', y2024: 0.8, y2025: 1.0 },
      { month: 'Jun', y2024: 0.5, y2025: 0.7 },
      { month: 'Jul', y2024: 0.9, y2025: 1.1 },
      { month: 'Ago', y2024: 0.7, y2025: 0.9 },
      { month: 'Set', y2024: 1.0, y2025: 1.2 },
      { month: 'Out', y2024: 0.8, y2025: 1.0 },
      { month: 'Nov', y2024: 0.6, y2025: 0.8 },
      { month: 'Dez', y2024: 0.8, y2025: 1.0 },
    ],
  };
  
  // Client colors
  const clientColors = {
    'Client A': '#0D1538',
    'Client B': '#2563EB',
    'Client C': '#059669',
    'Client D': '#D97706',
    'Client E': '#7C3AED',
  };
  
  // Get clients by country
  const getClientsByCountry = (countryId) => {
    return Object.entries(clientCountryMapping)
      .filter(([_, country]) => country === countryId)
      .map(([client]) => client);
  };
  
  // Get country by client
  const getCountryByClient = (clientName) => {
    return clientCountryMapping[clientName] || null;
  };
  
  // Check if client should be highlighted
  const isClientHighlighted = (clientName) => {
    if (selectedCountry) {
      return getClientsByCountry(selectedCountry).includes(clientName);
    }
    if (hoveredCountry) {
      return getClientsByCountry(hoveredCountry).includes(clientName);
    }
    if (hoveredClient) {
      return clientName === hoveredClient;
    }
    return true; // Default: all visible
  };
  
  // Check if country should be highlighted
  const isCountryHighlighted = (countryId) => {
    if (selectedCountry) {
      return countryId === selectedCountry;
    }
    if (hoveredClient) {
      return getCountryByClient(hoveredClient) === countryId;
    }
    if (hoveredCountry) {
      return countryId === hoveredCountry;
    }
    return true; // Default: all visible
  };
  
  // Handle country click (lock selection)
  const handleCountryClick = (countryId) => {
    if (selectedCountry === countryId) {
      setSelectedCountry(null); // Deselect if already selected
    } else {
      setSelectedCountry(countryId);
    }
  };
  
  // Reset selection
  const resetSelection = () => {
    setSelectedCountry(null);
    setHoveredCountry(null);
    setHoveredClient(null);
  };
  
  // ============ DATA SETUP ============
  const revenueData = mockData.revenueBreakdown[year];
  const customerData = mockData.customerConcentration[year];
  const prevRevenueData = mockData.revenueBreakdown[year - 1] || mockData.revenueBreakdown[2024];
  const prevCustomerData = mockData.customerConcentration[year - 1] || mockData.customerConcentration[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];
  
  // Budget values
  const budgetRevenue = budgetData.revenue?.budget || yearlyData.revenue * 1.03;
  const budgetVolume = 1150; // k carats budget
  const budgetAvgPrice = 259; // USD/ct budget
  const budgetSpecialMix = 20.0; // % budget
  const budgetTop5 = 62.0; // % budget
  
  // Calculate totals
  const totalCarats = revenueData.quantityByType.reduce((sum, item) => sum + item.carats, 0);
  const prevTotalCarats = prevRevenueData.quantityByType?.reduce((sum, item) => sum + item.carats, 0) || totalCarats * 0.88;
  const avgPrice = (yearlyData.revenue * 1000000 / totalCarats).toFixed(0);
  const prevAvgPrice = (prevYearData.revenue * 1000000 / prevTotalCarats).toFixed(0);
  
  // Variance calculations
  const revenueVsBudget = ((yearlyData.revenue - budgetRevenue) / budgetRevenue * 100);
  const revenueVsPrior = ((yearlyData.revenue - prevYearData.revenue) / prevYearData.revenue * 100);
  const volumeVsBudget = ((totalCarats / 1000 - budgetVolume) / budgetVolume * 100);
  const volumeVsPrior = ((totalCarats - prevTotalCarats) / prevTotalCarats * 100);
  const priceVsBudget = ((avgPrice - budgetAvgPrice) / budgetAvgPrice * 100);
  const priceVsPrior = ((avgPrice - prevAvgPrice) / prevAvgPrice * 100);
  const specialMix = revenueData.byType[1].percentage;
  const prevSpecialMix = prevRevenueData.byType[1].percentage;
  const specialMixVsBudget = specialMix - budgetSpecialMix;
  const specialMixVsPrior = specialMix - prevSpecialMix;
  const top5Pct = customerData[5]?.percentage || 64.2;
  const prevTop5Pct = prevCustomerData[5]?.percentage || 62.2;
  const top5VsBudget = top5Pct - budgetTop5;
  const top5VsPrior = top5Pct - prevTop5Pct;
  
  // Calculate Average Price (Regular + Special only) = (Regular Revenue + Special Revenue) / (Regular Volume + Special Volume)
  const regularRevenue = revenueData.byType[0].value;
  const specialRevenue = revenueData.byType[1].value;
  const regularVolume = (revenueData.byType[0].volume || totalCarats * 0.75) / 1000; // in k cts
  const specialVolume = (revenueData.byType[1].volume || totalCarats * 0.15) / 1000; // in k cts
  const avgPriceRegularSpecial = ((regularRevenue + specialRevenue) * 1000) / (regularVolume + specialVolume); // USD/ct
  
  const prevRegularRevenue = prevRevenueData.byType[0].value;
  const prevSpecialRevenue = prevRevenueData.byType[1].value;
  const prevRegularVolume = (prevRevenueData.byType[0].volume || prevTotalCarats * 0.75) / 1000;
  const prevSpecialVolume = (prevRevenueData.byType[1].volume || prevTotalCarats * 0.15) / 1000;
  const prevAvgPriceRegularSpecial = ((prevRegularRevenue + prevSpecialRevenue) * 1000) / (prevRegularVolume + prevSpecialVolume);
  
  // Budget for avg price (Regular + Special)
  const budgetRegularRevenue = budgetRevenue * 0.73;
  const budgetSpecialRevenue = budgetRevenue * 0.20;
  const budgetRegularVolume = budgetVolume * 0.75;
  const budgetSpecialVolume = budgetVolume * 0.15;
  const budgetAvgPriceRegularSpecial = ((budgetRegularRevenue + budgetSpecialRevenue) * 1000) / (budgetRegularVolume + budgetSpecialVolume);
  
  // Determine headline status
  const revenueStatus = revenueVsBudget >= 0 ? 'above' : 'below';
  const mainDriver = volumeVsPrior > priceVsPrior ? 'volume' : specialMixVsPrior > 1 ? 'mix' : 'price';
  const mainRisk = top5Pct > 65 ? 'concentration' : revenueData.byGeography[0].percentage > 50 ? 'geography' : 'none';

  // Executive Headline Messages
  const headlineMessages = {
    pt: {
      status: revenueStatus === 'above'
        ? `Receita ${revenueVsPrior > 0 ? 'acima' : 'abaixo'} de ${year-1} e ${Math.abs(revenueVsBudget).toFixed(0)}% acima do Orçamento ${year}`
        : `Receita ${revenueVsPrior > 0 ? 'acima' : 'abaixo'} de ${year-1}, mas ${Math.abs(revenueVsBudget).toFixed(0)}% abaixo do Orçamento ${year}`,
      driver: mainDriver === 'volume'
        ? `Crescimento suportado por volume (+${volumeVsPrior.toFixed(0)}% YoY) com aumento de produção`
        : mainDriver === 'mix'
        ? `Crescimento suportado por mix de diamantes especiais (+${specialMixVsPrior.toFixed(1)}p.p. YoY)`
        : `Crescimento suportado por preço médio (+${priceVsPrior.toFixed(0)}% YoY)`,
      risk: mainRisk === 'concentration'
        ? `Concentração Top 5 clientes em ${top5Pct.toFixed(0)}% - acima do target de ${budgetTop5}%`
        : mainRisk === 'geography'
        ? `Dependência geográfica elevada - ${revenueData.byGeography[0].region} representa ${revenueData.byGeography[0].percentage}%`
        : `Diversificação comercial em linha com objectivos estratégicos`
    },
    en: {
      status: revenueStatus === 'above'
        ? `Revenue ${revenueVsPrior > 0 ? 'above' : 'below'} ${year-1} and ${Math.abs(revenueVsBudget).toFixed(0)}% above ${year} Budget`
        : `Revenue ${revenueVsPrior > 0 ? 'above' : 'below'} ${year-1}, but ${Math.abs(revenueVsBudget).toFixed(0)}% below ${year} Budget`,
      driver: mainDriver === 'volume'
        ? `Growth supported by volume (+${volumeVsPrior.toFixed(0)}% YoY) with increased production`
        : mainDriver === 'mix'
        ? `Growth supported by special diamonds mix (+${specialMixVsPrior.toFixed(1)}p.p. YoY)`
        : `Growth supported by average price (+${priceVsPrior.toFixed(0)}% YoY)`,
      risk: mainRisk === 'concentration'
        ? `Top 5 client concentration at ${top5Pct.toFixed(0)}% - above ${budgetTop5}% target`
        : mainRisk === 'geography'
        ? `High geographic dependency - ${revenueData.byGeography[0].region} represents ${revenueData.byGeography[0].percentage}%`
        : `Commercial diversification in line with strategic objectives`
    }
  };

  // Revenue Bridge Data
  const revenueBridgeYoY = [
    { name: language === 'pt' ? `Receita ${year-1}` : `${year-1} Revenue`, value: prevYearData.revenue, type: 'start' },
    { name: language === 'pt' ? 'Vol. (Regulares)' : 'Vol. (Regular)', value: 18, type: 'positive' },
    { name: language === 'pt' ? 'Vol. (Especiais)' : 'Vol. (Special)', value: 8, type: 'positive' },
    { name: language === 'pt' ? 'Preço (Regulares)' : 'Price (Regular)', value: 12, type: 'positive' },
    { name: language === 'pt' ? 'Preço (Especiais)' : 'Price (Special)', value: 15, type: 'positive' },
    { name: language === 'pt' ? 'Rejeitados' : 'Rejected', value: 0, type: 'positive' },
    { name: language === 'pt' ? `Receita ${year}` : `${year} Revenue`, value: yearlyData.revenue, type: 'end' },
  ];

  const revenueBridgeBudget = [
    { name: language === 'pt' ? `Orçamento ${year}` : `${year} Budget`, value: budgetRevenue, type: 'start' },
    { name: language === 'pt' ? 'Vol. (Regulares)' : 'Vol. (Regular)', value: 5, type: 'positive' },
    { name: language === 'pt' ? 'Vol. (Especiais)' : 'Vol. (Special)', value: 2, type: 'positive' },
    { name: language === 'pt' ? 'Preço (Regulares)' : 'Price (Regular)', value: 3, type: 'positive' },
    { name: language === 'pt' ? 'Preço (Especiais)' : 'Price (Special)', value: 4, type: 'positive' },
    { name: language === 'pt' ? 'Rejeitados' : 'Rejected', value: -5, type: 'negative' },
    { name: language === 'pt' ? `Receita ${year}` : `${year} Revenue`, value: yearlyData.revenue, type: 'end' },
  ];

  const bridgeData = bridgeView === 'yoy' ? revenueBridgeYoY : revenueBridgeBudget;

  // Process bridge data for waterfall
  const processBridgeData = (data) => {
    let cumulative = 0;
    return data.map((item) => {
      if (item.type === 'start') {
        cumulative = item.value;
        return { ...item, start: 0, displayValue: item.value };
      } else if (item.type === 'end') {
        return { ...item, start: 0, displayValue: item.value };
      } else {
        const start = cumulative;
        cumulative += item.value;
        return {
          ...item,
          start: item.value > 0 ? start : cumulative,
          displayValue: item.value
        };
      }
    });
  };

  const processedBridgeData = processBridgeData(bridgeData);

  // Quarterly data with budget (Revenue and Volume)
  const quarterlyData = [
    { quarter: 'Q1', actual: 72.3, budget: 70.0, prior: 56.7, volActual: 301, volBudget: 286, volPrior: 271 },
    { quarter: 'Q2', actual: 75.5, budget: 76.0, prior: 61.9, volActual: 314, volBudget: 291, volPrior: 280 },
    { quarter: 'Q3', actual: 77.7, budget: 78.0, prior: 63.5, volActual: 322, volBudget: 294, volPrior: 285 },
    { quarter: 'Q4', actual: 72.5, budget: 75.3, prior: 63.0, volActual: 303, volBudget: 279, volPrior: 264 },
  ];

  // Quarterly data by diamond type (for hover popup)
  const quarterlyByType = {
    Regular: {
      revenue: [
        { quarter: 'Q1', actual: 53.2, budget: 51.5, prior: 44.8 },
        { quarter: 'Q2', actual: 55.1, budget: 55.0, prior: 46.5 },
        { quarter: 'Q3', actual: 56.8, budget: 56.5, prior: 48.2 },
        { quarter: 'Q4', actual: 52.9, budget: 54.2, prior: 45.5 },
      ],
      volume: [
        { quarter: 'Q1', actual: 228, budget: 216, prior: 205 },
        { quarter: 'Q2', actual: 235, budget: 218, prior: 210 },
        { quarter: 'Q3', actual: 242, budget: 220, prior: 215 },
        { quarter: 'Q4', actual: 225, budget: 208, prior: 195 },
      ]
    },
    Special: {
      revenue: [
        { quarter: 'Q1', actual: 15.8, budget: 14.2, prior: 11.2 },
        { quarter: 'Q2', actual: 16.5, budget: 15.0, prior: 12.1 },
        { quarter: 'Q3', actual: 17.2, budget: 15.5, prior: 12.8 },
        { quarter: 'Q4', actual: 15.5, budget: 14.8, prior: 11.9 },
      ],
      volume: [
        { quarter: 'Q1', actual: 45, budget: 42, prior: 40 },
        { quarter: 'Q2', actual: 47, budget: 44, prior: 42 },
        { quarter: 'Q3', actual: 49, budget: 45, prior: 43 },
        { quarter: 'Q4', actual: 45, budget: 42, prior: 40 },
      ]
    },
    'Low Grade': {
      revenue: [
        { quarter: 'Q1', actual: 3.3, budget: 4.3, prior: 2.7 },
        { quarter: 'Q2', actual: 3.9, budget: 5.0, prior: 3.3 },
        { quarter: 'Q3', actual: 3.7, budget: 6.0, prior: 2.5 },
        { quarter: 'Q4', actual: 4.1, budget: 5.3, prior: 3.5 },
      ],
      volume: [
        { quarter: 'Q1', actual: 28, budget: 28, prior: 26 },
        { quarter: 'Q2', actual: 32, budget: 29, prior: 28 },
        { quarter: 'Q3', actual: 31, budget: 29, prior: 27 },
        { quarter: 'Q4', actual: 33, budget: 29, prior: 29 },
      ]
    }
  };

  // Combine revenue and volume data for chart
  const getQuarterlyChartData = (type) => {
    const revenueData = quarterlyByType[type]?.revenue || [];
    const volumeData = quarterlyByType[type]?.volume || [];
    return revenueData.map((rev, idx) => ({
      quarter: rev.quarter,
      revenue2025: rev.actual,
      revenueBudget: rev.budget,
      revenue2024: rev.prior,
      volume2025: volumeData[idx]?.actual || 0,
      volumeBudget: volumeData[idx]?.budget || 0,
      volume2024: volumeData[idx]?.prior || 0,
    }));
  };

  // Revenue Table Data - Reorganized by category
  const revenueTableData = [
    // ===== RECEITAS TOTAIS =====
    { 
      label: { pt: 'Receitas Totais (M USD)', en: 'Total Revenue (M USD)' },
      actual: yearlyData.revenue,
      budget: budgetRevenue,
      prior: prevYearData.revenue,
      unit: 'M USD',
      type: 'header'
    },
    { 
      label: { pt: 'Pedras Regulares', en: 'Regular Stones' },
      actual: revenueData.byType[0].value,
      budget: budgetRevenue * 0.73,
      prior: prevRevenueData.byType[0].value,
      unit: 'M USD',
      type: 'row'
    },
    { 
      label: { pt: 'Pedras Especiais', en: 'Special Stones' },
      actual: revenueData.byType[1].value,
      budget: budgetRevenue * 0.20,
      prior: prevRevenueData.byType[1].value,
      unit: 'M USD',
      type: 'row'
    },
    { 
      label: { pt: 'Rejeitados', en: 'Rejected' },
      actual: revenueData.byType[2].value,
      budget: budgetRevenue * 0.07,
      prior: prevRevenueData.byType[2].value,
      unit: 'M USD',
      type: 'row'
    },
    // ===== VOLUME TOTAIS =====
    { 
      label: { pt: 'Volume Totais (k cts)', en: 'Total Volume (k cts)' },
      actual: totalCarats / 1000,
      budget: budgetVolume,
      prior: prevTotalCarats / 1000,
      unit: 'k cts',
      type: 'header'
    },
    { 
      label: { pt: 'Pedras Regulares', en: 'Regular Stones' },
      actual: (revenueData.byType[0].volume || totalCarats * 0.75) / 1000,
      budget: budgetVolume * 0.75,
      prior: (prevRevenueData.byType[0].volume || prevTotalCarats * 0.75) / 1000,
      unit: 'k cts',
      type: 'row'
    },
    { 
      label: { pt: 'Pedras Especiais', en: 'Special Stones' },
      actual: (revenueData.byType[1].volume || totalCarats * 0.15) / 1000,
      budget: budgetVolume * 0.15,
      prior: (prevRevenueData.byType[1].volume || prevTotalCarats * 0.15) / 1000,
      unit: 'k cts',
      type: 'row'
    },
    { 
      label: { pt: 'Rejeitados', en: 'Rejected' },
      actual: (revenueData.byType[2].volume || totalCarats * 0.10) / 1000,
      budget: budgetVolume * 0.10,
      prior: (prevRevenueData.byType[2].volume || prevTotalCarats * 0.10) / 1000,
      unit: 'k cts',
      type: 'row'
    },
    // ===== PREÇO MÉDIO (Regular + Especiais) =====
    { 
      label: { pt: 'Preço Médio (Regulares + Especiais) (USD/ct)', en: 'Avg Price (Regular + Special) (USD/ct)' },
      actual: Math.round(avgPriceRegularSpecial),
      budget: Math.round(budgetAvgPriceRegularSpecial),
      prior: Math.round(prevAvgPriceRegularSpecial),
      unit: 'USD/ct',
      type: 'header'
    },
    { 
      label: { pt: 'Preço Médio Regulares', en: 'Avg Regular Price' },
      actual: revenueData.avgPriceByType?.regular || 237,
      budget: 229,
      prior: prevRevenueData.avgPriceByType?.regular || 215,
      unit: 'USD/ct',
      type: 'row'
    },
    { 
      label: { pt: 'Preço Médio Especiais', en: 'Avg Special Price' },
      actual: revenueData.avgPriceByType?.special || 520,
      budget: 494,
      prior: prevRevenueData.avgPriceByType?.special || 453,
      unit: 'USD/ct',
      type: 'row'
    },
    { 
      label: { pt: 'Preço Médio Rejeitados', en: 'Avg Rejected Price' },
      actual: revenueData.avgPriceByType?.lowGrade || 120,
      budget: 115,
      prior: prevRevenueData.avgPriceByType?.lowGrade || 110,
      unit: 'USD/ct',
      type: 'row'
    },
  ];

  // KPI Card Component - Premium Style
  // Compact KPI Tooltip
  const KPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
    if (!isVisible) return null;
    return (
      <div 
        className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 top-full mt-2 left-1/2 -translate-x-1/2"
        style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
      >
        <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -top-1 border-t border-l" />
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
            <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
            <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
          </div>
          <div>
            <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
            <p className="text-white text-[11px] leading-snug">{definition}</p>
          </div>
          <div>
            <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
            <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
          </div>
          <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
            <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
            <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
          </div>
        </div>
      </div>
    );
  };

  const KPICard = ({ label, value, unit, vsBudget, vsPrior, inverse = false, highlight = false, tooltipData }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const budgetColor = getVarianceColor(vsBudget, inverse);
    const budgetBg = getVarianceBg(vsBudget, inverse);
    const priorColor = getVarianceColor(vsPrior, inverse);
    
    // Determine trend indicator based on YoY (vsPrior)
    // For revenue metrics, increase is positive (invertColors = true)
    const isPositiveOutcome = inverse ? vsPrior < 0 : vsPrior > 0;
    const trendBg = isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg;
    const trendColor = isPositiveOutcome ? colors.status.positive : colors.status.negative;
    const TrendIcon = vsPrior > 0 ? ArrowUpRight : vsPrior < 0 ? ArrowDownRight : Minus;
    
    // Format value: no decimals for board readability
    const formatKPIValue = (val) => {
      if (typeof val !== 'number') return val;
      if (unit === '%') return val.toFixed(1);
      return Math.round(val);
    };
    
    return (
      <div 
        className={`rounded-[14px] p-4 min-h-[150px] relative overflow-visible ${highlight ? 'border-2' : 'border'} ${tooltipData ? 'cursor-help' : ''}`}
        style={{ 
          backgroundColor: colors.bg.card, 
          borderColor: highlight ? colors.text.primary : colors.border.default 
        }}
        onMouseEnter={() => tooltipData && setShowTooltip(true)}
        onMouseLeave={() => tooltipData && setShowTooltip(false)}
      >
        {/* Trend Arrow Indicator - Positioned lower to not overlap text */}
        <div 
          className="absolute top-12 right-3 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: trendBg }}
        >
          <TrendIcon className="w-3.5 h-3.5" style={{ color: trendColor }} />
        </div>
        
        <p 
          className="text-[11px] leading-[14px] font-medium tracking-[0.2px] mb-2"
          style={{ color: colors.text.tertiary }}
        >
          {label}
        </p>
        <p 
          className="text-[28px] leading-[32px] font-semibold tracking-[-0.5px]"
          style={{ color: colors.text.primary, fontFamily: 'Inter, sans-serif' }}
        >
          {formatKPIValue(value)}
          <span 
            className="text-[12px] leading-[16px] font-normal ml-1"
            style={{ color: colors.text.tertiary }}
          >
            {unit}
          </span>
        </p>
        <div className="mt-3 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <span 
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold"
              style={{ backgroundColor: budgetBg, color: budgetColor }}
            >
              {vsBudget >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {vsBudget >= 0 ? '+' : ''}{Math.round(vsBudget)}{unit === '%' ? 'p.p.' : '%'} vs Orç.
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span 
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold"
              style={{ 
                backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg,
                color: priorColor 
              }}
            >
              {vsPrior >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {vsPrior >= 0 ? '+' : ''}{Math.round(vsPrior)}{unit === '%' ? 'p.p.' : '%'} YoY
            </span>
          </div>
        </div>
        
        {/* Compact Tooltip */}
        {tooltipData && (
          <KPITooltip 
            isVisible={showTooltip}
            title={tooltipData.title}
            definition={tooltipData.definition}
            relevance={tooltipData.relevance}
            currentReading={tooltipData.currentReading}
          />
        )}
      </div>
    );
  };

  // Bridge Tooltip - Premium Style
  const BridgeTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isTotal = data.type === 'start' || data.type === 'end';
      return (
        <div 
          className="rounded-[10px] px-4 py-3 shadow-xl"
          style={{ backgroundColor: colors.bg.dark, border: `1px solid ${colors.border.default}` }}
        >
          <p className="text-[14px] font-semibold mb-1" style={{ color: colors.accent.gold }}>{data.name}</p>
          <p 
            className="text-[18px] font-semibold"
            style={{ color: isTotal ? '#FFFFFF' : data.displayValue > 0 ? colors.status.positive : colors.status.negative }}
          >
            {isTotal ? '' : data.displayValue > 0 ? '+' : ''}{formatValue(data.displayValue)}M USD
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8" data-testid="revenue-analysis-section" style={{ backgroundColor: colors.bg.page, minHeight: '100vh', padding: '0' }}>
      
      {/* ============ PREMIUM HEADER ============ */}
      <div>
        <button 
          onClick={() => setActiveSection('performance')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Receitas' : 'Revenue'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* ============ KEY MESSAGES BAND - CAPEX Style ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {/* Resultado */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/30">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-gold-400">
                  {language === 'pt' ? 'RECEITA' : 'REVENUE'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Receita de ${formatValue(yearlyData.revenue)}M USD, ${revenueVsBudget >= 0 ? '+' : ''}${Math.round(revenueVsBudget)}% Orçamento e +${Math.round(revenueVsPrior)}% YoY, superando expectativas.`
                    : `Revenue of ${formatValue(yearlyData.revenue)}M USD, ${revenueVsBudget >= 0 ? '+' : ''}${Math.round(revenueVsBudget)}% Budget and +${Math.round(revenueVsPrior)}% YoY, exceeding expectations.`
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Drivers */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-500/30">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-emerald-400">
                  {language === 'pt' ? 'DRIVERS' : 'DRIVERS'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? 'Crescimento suportado por volume e melhoria do mix de pedras especiais, com preco medio a subir.'
                    : 'Growth supported by volume and improved special stones mix, with average price increasing.'
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Atencao */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-500/30">
                <AlertTriangle className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-blue-400">
                  {language === 'pt' ? 'CONCENTRACAO' : 'CONCENTRATION'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Top 5 clientes em ${top5Pct.toFixed(1)}%, acima do target de ${budgetTop5}%. Monitorizar risco de concentracao.`
                    : `Top 5 customers at ${top5Pct.toFixed(1)}%, above ${budgetTop5}% target. Monitor concentration risk.`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ KPI CARDS (7 - Pedras Regulares e Especiais) ============ */}
      <div className="grid grid-cols-7 gap-3">
        <KPICard 
          label={language === 'pt' ? 'Receitas Totais' : 'Total Revenue'}
          value={yearlyData.revenue}
          unit="M USD"
          vsBudget={revenueVsBudget}
          vsPrior={revenueVsPrior}
          highlight={true}
        />
        <KPICard 
          label={language === 'pt' ? 'Receitas Pedras Regulares' : 'Regular Stones Revenue'}
          value={revenueData.byType[0].value}
          unit="M USD"
          vsBudget={((revenueData.byType[0].value - budgetRevenue * 0.73) / (budgetRevenue * 0.73) * 100)}
          vsPrior={((revenueData.byType[0].value - prevRevenueData.byType[0].value) / prevRevenueData.byType[0].value * 100)}
        />
        <KPICard 
          label={language === 'pt' ? 'Volume Pedras Regulares' : 'Regular Stones Volume'}
          value={930}
          unit="kct"
          vsBudget={7.9}
          vsPrior={12.7}
        />
        <KPICard 
          label={language === 'pt' ? 'Preço Pedras Regulares' : 'Regular Stones Price'}
          value={revenueData.avgPriceByType?.regular || 237}
          unit="USD/ct"
          vsBudget={3.5}
          vsPrior={10.2}
        />
        <KPICard 
          label={language === 'pt' ? 'Receitas Pedras Especiais' : 'Special Stones Revenue'}
          value={revenueData.byType[1].value}
          unit="M USD"
          vsBudget={((revenueData.byType[1].value - budgetRevenue * 0.20) / (budgetRevenue * 0.20) * 100)}
          vsPrior={((revenueData.byType[1].value - prevRevenueData.byType[1].value) / prevRevenueData.byType[1].value * 100)}
        />
        <KPICard 
          label={language === 'pt' ? 'Volume Pedras Especiais' : 'Special Stones Volume'}
          value={186}
          unit="kct"
          vsBudget={7.5}
          vsPrior={12.7}
        />
        <KPICard 
          label={language === 'pt' ? 'Preço Pedras Especiais' : 'Special Stones Price'}
          value={revenueData.avgPriceByType?.special || 520}
          unit="USD/ct"
          vsBudget={5.2}
          vsPrior={14.8}
        />
      </div>

      {/* ===================== */}
      {/* SUMÁRIO EXECUTIVO */}
      {/* ===================== */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Análise de receitas e desempenho comercial' : 'Revenue analysis and commercial performance'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="revenue-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Em ${year}, a receita total foi de USD ${formatValue(yearlyData.revenue)} milhões, suportada quase integralmente por pedras regulares e especiais, com ${formatValue(totalCarats / 1000)} k qlt vendidos e preço médio de USD ${Math.round(yearlyData.revenue * 1000000 / totalCarats)}/qlt. O desempenho comercial superou o orçamento e o período homólogo, reflectindo execução disciplinada e condições de mercado favoráveis.`
              : `In ${year}, total revenue was USD ${formatValue(yearlyData.revenue)} million, supported almost entirely by regular and special stones, with ${formatValue(totalCarats / 1000)}k carats sold and average price of USD ${Math.round(yearlyData.revenue * 1000000 / totalCarats)}/ct. Commercial performance exceeded budget and prior year, reflecting disciplined execution and favorable market conditions.`}
          </p>
        </div>

        {/* Driver Sections - Collapsible Cards */}
        <div className="space-y-4">
          {/* Trimestre mais forte */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Trimestre mais Forte' : 'Strongest Quarter'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Performance trimestral destacada' : 'Highlighted quarterly performance'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O trimestre mais forte de ${year} foi o T3, com receita de USD 85M, +18% acima do período homólogo e +12% acima do orçamento. O diferencial face ao homólogo resultou de volume e mix, enquanto o desvio face ao orçamento reflectiu sobretudo execução comercial e maior peso de pedras especiais.`
                : `The strongest quarter of ${year} was Q3, with revenue of USD 85M, +18% above the same period last year and +12% above budget. The difference vs prior year resulted from volume and mix, while the budget variance mainly reflected commercial execution and higher weight of special stones.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Métricas T3' : 'Q3 Metrics'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Receita: USD 85M' : 'Revenue: USD 85M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'vs Homólogo: ' : 'vs Prior Year: '}<span className="text-emerald-600 font-semibold">+18%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'vs Orçamento: ' : 'vs Budget: '}<span className="text-emerald-600 font-semibold">+12%</span>
                </li>
              </ul>
            </div>
          </div>

          {/* 3 Meses de maior receita */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? '3 Meses de Maior Receita' : 'Top 3 Revenue Months'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Concentração mensal de receita' : 'Monthly revenue concentration'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Os três meses de maior receita — Março, Julho e Setembro — concentraram 42% da receita anual, acima do nível implícito de uma distribuição homogénea. Em conjunto, ficaram +15% acima do período homólogo e +8% acima do orçamento. Face ao homólogo, o diferencial resultou sobretudo de maior volume vendido e mix mais favorável.`
                : `The three highest revenue months — March, July and September — concentrated 42% of annual revenue, above the level implied by a homogeneous distribution. Together, they were +15% above the same period last year and +8% above budget. The variance vs prior year resulted mainly from higher volume sold and more favorable mix.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Análise vs Orçamento e YoY' : 'Analysis vs Budget and YoY'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Meses: Mar, Jul, Set' : 'Months: Mar, Jul, Sep'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Peso anual: 42%' : 'Annual weight: 42%'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'vs Homólogo: ' : 'vs Prior Year: '}<span className="text-emerald-600 font-semibold">+15%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'vs Orçamento: ' : 'vs Budget: '}<span className="text-emerald-600 font-semibold">+8%</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pedras Regulares */}
          <div className="bg-gradient-to-r from-slate-50 to-zinc-50 rounded-xl p-5 border border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Gem className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Pedras Regulares' : 'Regular Stones'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Segmento core de receita' : 'Core revenue segment'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A receita de ${year} das pedras regulares foi de USD ${formatValue(revenueData.byType[0].value)}M, +${Math.round(((revenueData.byType[0].value - prevRevenueData.byType[0].value) / prevRevenueData.byType[0].value) * 100)}% face ao período homólogo e +${Math.round(((revenueData.byType[0].value - budgetRevenue * 0.73) / (budgetRevenue * 0.73)) * 100)}% vs orçamento. O diferencial explica-se sobretudo por volume e cadência de vendas.`
                : `${year} regular stones revenue was USD ${formatValue(revenueData.byType[0].value)}M, +${Math.round(((revenueData.byType[0].value - prevRevenueData.byType[0].value) / prevRevenueData.byType[0].value) * 100)}% vs prior year and +${Math.round(((revenueData.byType[0].value - budgetRevenue * 0.73) / (budgetRevenue * 0.73)) * 100)}% budget. The variance is mainly explained by volume and sales cadence.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Métricas Regulares' : 'Regular Metrics'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                  {language === 'pt' ? `Receita: USD ${formatValue(revenueData.byType[0].value)}M` : `Revenue: USD ${formatValue(revenueData.byType[0].value)}M`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                  {language === 'pt' ? 'vs Homólogo: ' : 'vs Prior Year: '}<span className="text-emerald-600 font-semibold">+{Math.round(((revenueData.byType[0].value - prevRevenueData.byType[0].value) / prevRevenueData.byType[0].value) * 100)}%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                  {language === 'pt' ? 'vs Orçamento: ' : 'vs Budget: '}<span className="text-emerald-600 font-semibold">+{Math.round(((revenueData.byType[0].value - budgetRevenue * 0.73) / (budgetRevenue * 0.73)) * 100)}%</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pedras Especiais */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Gem className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Pedras Especiais' : 'Special Stones'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Segmento premium de alto valor' : 'Premium high-value segment'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A receita de ${year} das pedras especiais foi de USD ${formatValue(revenueData.byType[1].value)}M, +${Math.round(((revenueData.byType[1].value - prevRevenueData.byType[1].value) / prevRevenueData.byType[1].value) * 100)}% face ao período homólogo e +${Math.round(((revenueData.byType[1].value - budgetRevenue * 0.20) / (budgetRevenue * 0.20)) * 100)}% vs orçamento. O diferencial explica-se sobretudo por preço, mix e peso de vendas excepcionais.`
                : `${year} special stones revenue was USD ${formatValue(revenueData.byType[1].value)}M, +${Math.round(((revenueData.byType[1].value - prevRevenueData.byType[1].value) / prevRevenueData.byType[1].value) * 100)}% vs prior year and +${Math.round(((revenueData.byType[1].value - budgetRevenue * 0.20) / (budgetRevenue * 0.20)) * 100)}% budget. The variance is mainly explained by price, mix and exceptional sales weight.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Métricas Especiais' : 'Special Metrics'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? `Receita: USD ${formatValue(revenueData.byType[1].value)}M` : `Revenue: USD ${formatValue(revenueData.byType[1].value)}M`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'vs Homólogo: ' : 'vs Prior Year: '}<span className="text-emerald-600 font-semibold">+{Math.round(((revenueData.byType[1].value - prevRevenueData.byType[1].value) / prevRevenueData.byType[1].value) * 100)}%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'vs Orçamento: ' : 'vs Budget: '}<span className="text-emerald-600 font-semibold">+{Math.round(((revenueData.byType[1].value - budgetRevenue * 0.20) / (budgetRevenue * 0.20)) * 100)}%</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Meses com melhor preço - Regulares */}
          <div className="bg-gradient-to-r from-violet-50 to-slate-50 rounded-xl p-5 border border-violet-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Meses com Melhor Preço — Regulares' : 'Best Price Months — Regular'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Análise de pricing por período' : 'Pricing analysis by period'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Os meses com melhor preço em pedras regulares — Fevereiro, Maio e Agosto — ficaram +8% acima do período homólogo e +5% acima do orçamento. A explicação centra-se em qualidade média dos lotes vendidos e melhor disciplina comercial na colocação do volume.`
                : `The months with the best price in regular stones — February, May and August — were +8% above the same period last year and +5% above budget. The explanation centers on average lot quality sold and better commercial discipline in volume placement.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-violet-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Análise de Preço' : 'Price Analysis'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  {language === 'pt' ? 'Meses: Fev, Mai, Ago' : 'Months: Feb, May, Aug'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  {language === 'pt' ? 'vs Homólogo: ' : 'vs Prior Year: '}<span className="text-emerald-600 font-semibold">+8%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  {language === 'pt' ? 'vs Orçamento: ' : 'vs Budget: '}<span className="text-emerald-600 font-semibold">+5%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  {language === 'pt' ? 'Driver: Qualidade/Disciplina' : 'Driver: Quality/Discipline'}
                </li>
              </ul>
            </div>
          </div>

          {/* Meses com melhor preço - Especiais */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Meses com Melhor Preço — Especiais' : 'Best Price Months — Special'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Pricing premium de alto valor' : 'Premium high-value pricing'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Nos meses com melhor preço em pedras especiais — Abril, Julho e Novembro — ficaram +22% acima do homólogo e +15% acima do orçamento. O desvio explica-se por mix, presença de pedras de valor elevado e concentração de eventos comerciais de maior preço unitário.`
                : `In the months with the best price in special stones — April, July and November — were +22% above prior year and +15% above budget. The variance is explained by mix, presence of high-value stones and concentration of higher unit price commercial events.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-200">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Análise de Preço Premium' : 'Premium Price Analysis'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Meses: Abr, Jul, Nov' : 'Months: Apr, Jul, Nov'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'vs Homólogo: ' : 'vs Prior Year: '}<span className="text-emerald-600 font-semibold">+22%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'vs Orçamento: ' : 'vs Budget: '}<span className="text-emerald-600 font-semibold">+15%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Driver: Mix/Pedras excepcionais' : 'Driver: Mix/Exceptional stones'}
                </li>
              </ul>
            </div>
          </div>

          {/* Concentração: 3 maiores vendas */}
          <div className="bg-gradient-to-r from-rose-50 to-slate-50 rounded-xl p-5 border border-rose-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Concentração: 3 Maiores Vendas' : 'Concentration: Top 3 Sales'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Análise de risco de concentração' : 'Concentration risk analysis'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `As três maiores vendas (${customerData[0]?.customer}, ${customerData[1]?.customer}, ${customerData[2]?.customer}) representaram ${(customerData[0]?.percentage + customerData[1]?.percentage + customerData[2]?.percentage).toFixed(0)}% da receita anual, ficando +12% acima do homólogo. A principal leitura para a gerência é perceber se estas vendas confirmam reforço da base comercial ou maior dependência de um número reduzido de transacções.`
                : `The three largest sales (${customerData[0]?.customer}, ${customerData[1]?.customer}, ${customerData[2]?.customer}) represented ${(customerData[0]?.percentage + customerData[1]?.percentage + customerData[2]?.percentage).toFixed(0)}% of annual revenue, +12% above prior year. The main takeaway for management is to understand if these sales confirm a stronger commercial base or greater dependence on a small number of transactions.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-rose-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Indicadores de Concentração' : 'Concentration Indicators'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  {language === 'pt' ? `Top 3: ${customerData[0]?.customer}, ${customerData[1]?.customer}, ${customerData[2]?.customer}` : `Top 3: ${customerData[0]?.customer}, ${customerData[1]?.customer}, ${customerData[2]?.customer}`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  {language === 'pt' ? `Peso anual: ${(customerData[0]?.percentage + customerData[1]?.percentage + customerData[2]?.percentage).toFixed(0)}%` : `Annual weight: ${(customerData[0]?.percentage + customerData[1]?.percentage + customerData[2]?.percentage).toFixed(0)}%`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  {language === 'pt' ? 'vs Homólogo: ' : 'vs Prior Year: '}<span className="text-emerald-600 font-semibold">+12%</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                  {language === 'pt' ? 'Leitura: ' : 'Reading: '}<span className="text-amber-600 font-semibold">{language === 'pt' ? 'Atenção à concentração' : 'Attention to concentration'}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* ============ DRILLDOWN NAVIGATION CARDS ============ */}
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'pt' ? 'Análises Relacionadas' : 'Related Analysis'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <ExecutiveDrilldownCard
                icon={BarChart3}
                title={language === 'pt' ? 'Impacto no EBITDA' : 'EBITDA Impact'}
                subtitle={language === 'pt' ? 'Como a receita formou margem' : 'How revenue formed margin'}
                navigateTo="ebitda-detail"
                setActiveSection={setActiveSection}
                color="blue"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'A receita é o principal driver do EBITDA. Veja como a performance comercial contribuiu para a margem operacional.'
                    : 'Revenue is the main EBITDA driver. See how commercial performance contributed to operating margin.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={Target}
                title={language === 'pt' ? 'Custos de Produção' : 'Production Costs'}
                subtitle={language === 'pt' ? 'CMVMC e margem bruta' : 'COGS and gross margin'}
                navigateTo="opex-cogs"
                setActiveSection={setActiveSection}
                color="amber"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise como os custos de produção e comercialização impactaram a margem bruta das vendas realizadas.'
                    : 'Analyze how production and sales costs impacted the gross margin of completed sales.'}
                </p>
              </ExecutiveDrilldownCard>
            </div>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* ============ CHARTS: REVENUE BY TYPE & QUARTERLY ============ */}
      <div className="grid grid-cols-9 gap-6">
        {/* REVENUE BY TYPE - Vertical (col-span-4 = ~44%) */}
        <div 
          className="col-span-4 rounded-[18px] p-6 overflow-visible"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          <h3 
            className="text-[24px] leading-[30px] font-semibold tracking-[-0.2px] mb-4"
            style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
          >
            {language === 'pt' ? 'Receita e Volume por Tipo de Pedra' : 'Revenue & Volume by Stone Type'}
          </h3>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setStoneTypeView('yoy')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                stoneTypeView === 'yoy' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              vs {year - 1}
            </button>
            <button
              onClick={() => setStoneTypeView('budget')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                stoneTypeView === 'budget' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              vs {language === 'pt' ? 'Orçamento' : 'Budget'}
            </button>
          </div>
          <div className="flex flex-col gap-3 overflow-visible">
            {revenueData.byType.map((item, idx) => {
              const budgetValue = idx === 0 ? budgetRevenue * 0.73 : idx === 1 ? budgetRevenue * 0.20 : budgetRevenue * 0.07;
              const prevItem = prevRevenueData.byType[idx];
              const vsBudget = ((item.value - budgetValue) / budgetValue * 100);
              const vsPrior = ((item.value - prevItem.value) / prevItem.value * 100);
              const typeName = item.type === 'Regular' ? (language === 'pt' ? 'Regulares' : 'Regular') :
                              item.type === 'Special' ? (language === 'pt' ? 'Especiais' : 'Special') :
                              (language === 'pt' ? 'Rejeitados' : 'Rejected');
              const bgColor = idx === 0 ? colors.bg.header : idx === 1 ? colors.accent.gold : colors.text.tertiary;
              const chartData = getQuarterlyChartData(item.type);
              
              // Volume data
              const volumeActual = idx === 0 ? 930 : idx === 1 ? 186 : 124; // k cts
              const volumeBudget = idx === 0 ? 862 : idx === 1 ? 173 : 115;
              const volumePrior = idx === 0 ? 825 : idx === 1 ? 165 : 110;
              const volumePct = idx === 0 ? 75 : idx === 1 ? 15 : 10; // mix %
              const volVsBudget = ((volumeActual - volumeBudget) / volumeBudget * 100);
              const volVsPrior = ((volumeActual - volumePrior) / volumePrior * 100);
              
              // Select comparison based on toggle
              const revenueVar = stoneTypeView === 'yoy' ? vsPrior : vsBudget;
              const volumeVar = stoneTypeView === 'yoy' ? volVsPrior : volVsBudget;
              const compLabel = stoneTypeView === 'yoy' ? `vs ${year - 1}` : 'vs Orç';
              
              return (
                <div 
                  key={idx} 
                  className="border border-slate-200 rounded-lg overflow-visible relative cursor-pointer transition-shadow hover:shadow-lg"
                  onMouseEnter={() => setHoveredType(item.type)}
                  onMouseLeave={() => setHoveredType(null)}
                  data-testid={`diamond-type-card-${item.type.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex">
                    {/* Type Name Column - altura fixa */}
                    <div className="px-3 py-4 rounded-l-lg flex items-center justify-center" style={{ backgroundColor: bgColor, width: '95px' }}>
                      <p className="text-white font-semibold text-sm text-center">{typeName}</p>
                    </div>
                    
                    {/* Data Column - Grid structure for alignment */}
                    <div className="flex-1 px-2 py-1.5">
                      {/* Revenue Row */}
                      <div className="grid grid-cols-[70px_45px_1fr] items-center gap-1 py-1 border-b border-slate-100">
                        <span className="text-sm font-semibold text-primary tabular-nums">{item.value}M</span>
                        <span className="text-[10px] text-slate-400 tabular-nums">{item.percentage}%</span>
                        <div className="flex justify-end text-[10px]">
                          <span 
                            className="px-1.5 py-0.5 rounded font-medium tabular-nums text-center"
                            style={{ 
                              backgroundColor: revenueVar >= 0 ? colors.status.positiveBg : colors.status.negativeBg,
                              color: revenueVar >= 0 ? colors.status.positive : colors.status.negative
                            }}
                          >
                            {revenueVar >= 0 ? '+' : ''}{revenueVar.toFixed(0)}% {compLabel}
                          </span>
                        </div>
                      </div>
                      
                      {/* Volume Row */}
                      <div className="grid grid-cols-[70px_45px_1fr] items-center gap-1 py-1">
                        <span className="text-sm font-semibold text-primary tabular-nums whitespace-nowrap">{volumeActual}<span className="text-[10px] text-slate-400 ml-0.5">k</span></span>
                        <span className="text-[10px] text-slate-400 tabular-nums">{volumePct}%</span>
                        <div className="flex justify-end text-[10px]">
                          <span 
                            className="px-1.5 py-0.5 rounded font-medium tabular-nums text-center"
                            style={{ 
                              backgroundColor: volumeVar >= 0 ? colors.status.positiveBg : colors.status.negativeBg,
                              color: volumeVar >= 0 ? colors.status.positive : colors.status.negative
                            }}
                          >
                            {volumeVar >= 0 ? '+' : ''}{volumeVar.toFixed(0)}% {compLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Popup Chart on Hover */}
                  {hoveredType === item.type && (
                    <div 
                      className="absolute z-50 bg-white rounded-xl shadow-2xl border border-slate-200 p-4"
                      style={{ 
                        top: '50%', 
                        left: '100%', 
                        transform: 'translateY(-50%)',
                        marginLeft: '12px',
                        width: '380px'
                      }}
                    >
                      {/* Arrow pointing left */}
                      <div 
                        className="absolute top-1/2 -left-2 transform -translate-y-1/2 w-4 h-4 bg-white border-l border-b border-slate-200 rotate-45"
                      ></div>
                      
                      <div className="relative">
                        <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-3">
                          {language === 'pt' ? `${typeName} - Evolução Trimestral` : `${typeName} - Quarterly Evolution`}
                        </p>
                        
                        <ResponsiveContainer width="100%" height={160}>
                          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                            <XAxis 
                              dataKey="quarter" 
                              tick={{ fontSize: 10, fill: colors.text.secondary }} 
                              stroke="transparent"
                            />
                            <YAxis 
                              yAxisId="revenue"
                              tick={{ fontSize: 9, fill: colors.text.tertiary }} 
                              stroke="transparent"
                              tickFormatter={(v) => `${v}M`}
                              domain={[0, 'auto']}
                            />
                            <YAxis 
                              yAxisId="volume"
                              orientation="right"
                              tick={{ fontSize: 9, fill: colors.text.tertiary }} 
                              stroke="transparent"
                              tickFormatter={(v) => `${v}`}
                              domain={[0, 'auto']}
                            />
                            <Tooltip 
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <div className="bg-[#0A192F] text-white rounded-lg px-3 py-2 shadow-xl text-xs">
                                      <p className="font-semibold text-gold-400 mb-1.5">{label}</p>
                                      <div className="space-y-1">
                                        <p><span className="text-slate-400">{year}:</span> <span className="font-semibold">{payload[0]?.value?.toFixed(1)}M</span></p>
                                        <p><span className="text-slate-400">{language === 'pt' ? 'Orç.' : 'Bdg.'}:</span> {payload[1]?.value?.toFixed(1)}M</p>
                                        <p><span className="text-slate-400">{year - 1}:</span> {payload[2]?.value?.toFixed(1)}M</p>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            {/* Revenue Bars */}
                            <Bar yAxisId="revenue" dataKey="revenue2025" fill={colors.chart.primary} radius={[2, 2, 0, 0]} barSize={16} />
                            <Bar yAxisId="revenue" dataKey="revenueBudget" fill={colors.chart.budget} radius={[2, 2, 0, 0]} barSize={16} />
                            <Bar yAxisId="revenue" dataKey="revenue2024" fill={colors.chart.prior} radius={[2, 2, 0, 0]} barSize={16} />
                            {/* Volume Lines */}
                            <Line yAxisId="volume" type="monotone" dataKey="volume2025" stroke={colors.chart.primary} strokeWidth={2} dot={{ r: 3, fill: colors.chart.primary }} />
                            <Line yAxisId="volume" type="monotone" dataKey="volumeBudget" stroke={colors.chart.budget} strokeWidth={2} strokeDasharray="4 2" dot={{ r: 3, fill: colors.chart.budget }} />
                            <Line yAxisId="volume" type="monotone" dataKey="volume2024" stroke={colors.chart.prior} strokeWidth={2} dot={{ r: 3, fill: colors.chart.prior }} />
                          </ComposedChart>
                        </ResponsiveContainer>
                        
                        {/* Legend em 2 linhas como o gráfico principal */}
                        <div className="mt-3 space-y-1.5">
                          {/* Linha 1: Barras (Receita) */}
                          <div className="flex justify-center items-center gap-3 text-[9px]">
                            <span className="text-slate-500 font-medium">{language === 'pt' ? 'Receita:' : 'Revenue:'}</span>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: colors.chart.primary }}></div>
                              <span>{year}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: colors.chart.budget }}></div>
                              <span>{language === 'pt' ? 'Orç.' : 'Bdg.'} {year}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: colors.chart.prior }}></div>
                              <span>{year - 1}</span>
                            </div>
                          </div>
                          {/* Linha 2: Linhas (Volume) */}
                          <div className="flex justify-center items-center gap-3 text-[9px]">
                            <span className="text-slate-500 font-medium">{language === 'pt' ? 'Volume:' : 'Volume:'}</span>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-0.5" style={{ backgroundColor: colors.chart.primary }}></div>
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.chart.primary }}></div>
                              <span>{year}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-0.5" style={{ backgroundColor: colors.chart.budget }}></div>
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.chart.budget }}></div>
                              <span>{language === 'pt' ? 'Orç.' : 'Bdg.'} {year}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-0.5" style={{ backgroundColor: colors.chart.prior }}></div>
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.chart.prior }}></div>
                              <span>{year - 1}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* TOTAL Row */}
            <div 
              className="border-2 border-primary rounded-lg overflow-visible mt-2"
              data-testid="diamond-type-card-total"
            >
              <div className="flex">
                {/* Total Label Column */}
                <div className="px-3 py-4 rounded-l-lg flex items-center justify-center bg-primary" style={{ width: '95px' }}>
                  <p className="text-white font-bold text-sm text-center">TOTAL</p>
                </div>
                
                {/* Total Data Column */}
                <div className="flex-1 px-2 py-1.5 bg-slate-50">
                  {/* Revenue Total Row */}
                  <div className="grid grid-cols-[70px_45px_1fr] items-center gap-1 py-1 border-b border-slate-200">
                    <span className="text-sm font-bold text-primary tabular-nums">
                      {revenueData.byType.reduce((sum, item) => sum + item.value, 0)}M
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium tabular-nums">100%</span>
                    <div className="flex justify-end text-[10px]">
                      {(() => {
                        const totalActual = revenueData.byType.reduce((sum, item) => sum + item.value, 0);
                        const totalBudget = budgetRevenue;
                        const totalPrior = prevRevenueData.byType.reduce((sum, item) => sum + item.value, 0);
                        const totalVar = stoneTypeView === 'yoy' 
                          ? ((totalActual - totalPrior) / totalPrior * 100)
                          : ((totalActual - totalBudget) / totalBudget * 100);
                        const compLabel = stoneTypeView === 'yoy' ? `vs ${year - 1}` : 'vs Orç';
                        return (
                          <span 
                            className="px-1.5 py-0.5 rounded font-semibold tabular-nums text-center"
                            style={{ 
                              backgroundColor: totalVar >= 0 ? colors.status.positiveBg : colors.status.negativeBg,
                              color: totalVar >= 0 ? colors.status.positive : colors.status.negative
                            }}
                          >
                            {totalVar >= 0 ? '+' : ''}{totalVar.toFixed(0)}% {compLabel}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Volume Total Row */}
                  <div className="grid grid-cols-[70px_45px_1fr] items-center gap-1 py-1">
                    <span className="text-sm font-bold text-primary tabular-nums whitespace-nowrap">
                      {930 + 186 + 124}<span className="text-[10px] text-slate-400 ml-0.5">k</span>
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium tabular-nums">100%</span>
                    <div className="flex justify-end text-[10px]">
                      {(() => {
                        const volTotalActual = 930 + 186 + 124;
                        const volTotalBudget = 862 + 173 + 115;
                        const volTotalPrior = 825 + 165 + 110;
                        const volTotalVar = stoneTypeView === 'yoy'
                          ? ((volTotalActual - volTotalPrior) / volTotalPrior * 100)
                          : ((volTotalActual - volTotalBudget) / volTotalBudget * 100);
                        const compLabel = stoneTypeView === 'yoy' ? `vs ${year - 1}` : 'vs Orç';
                        return (
                          <span 
                            className="px-1.5 py-0.5 rounded font-semibold tabular-nums text-center"
                            style={{ 
                              backgroundColor: volTotalVar >= 0 ? colors.status.positiveBg : colors.status.negativeBg,
                              color: volTotalVar >= 0 ? colors.status.positive : colors.status.negative
                            }}
                          >
                            {volTotalVar >= 0 ? '+' : ''}{volTotalVar.toFixed(0)}% {compLabel}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* QUARTERLY - (col-span-5 = ~56%) */}
        <div 
          className="col-span-5 rounded-[18px] p-6"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 
              className="text-[22px] leading-[28px] font-semibold"
              style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Evolução Trimestral' : 'Quarterly Evolution'}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setQuarterlyView('yoy')}
                className="px-4 py-2 text-[14px] font-medium transition-colors"
                style={{ 
                  backgroundColor: quarterlyView === 'yoy' ? colors.bg.dark : '#F1F4F9',
                  color: quarterlyView === 'yoy' ? '#FFFFFF' : colors.text.secondary,
                  borderRadius: '10px',
                  border: quarterlyView === 'yoy' ? 'none' : `1px solid ${colors.border.default}`
                }}
              >
                vs {year - 1}
              </button>
              <button
                onClick={() => setQuarterlyView('budget')}
                className="px-4 py-2 text-[14px] font-medium transition-colors"
                style={{ 
                  backgroundColor: quarterlyView === 'budget' ? colors.bg.dark : '#F1F4F9',
                  color: quarterlyView === 'budget' ? '#FFFFFF' : colors.text.secondary,
                  borderRadius: '10px',
                  border: quarterlyView === 'budget' ? 'none' : `1px solid ${colors.border.default}`
                }}
              >
                vs {language === 'pt' ? 'Orçamento' : 'Budget'}
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={quarterlyData} margin={{ top: 10, right: 45, left: 5, bottom: 10 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: colors.text.secondary }} stroke="transparent" tickFormatter={(q) => formatQuarter(q, language)} />
              <YAxis 
                yAxisId="revenue"
                tick={{ fontSize: 10, fill: colors.text.tertiary }} 
                stroke="transparent" 
                domain={[0, 100]} 
                tickFormatter={(v) => `${v}M`} 
              />
              <YAxis 
                yAxisId="volume"
                orientation="right"
                tick={{ fontSize: 10, fill: colors.text.tertiary }} 
                stroke="transparent" 
                domain={[0, 400]} 
                tickFormatter={(v) => `${v}`}
              />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const actual = payload.find(p => p.dataKey === 'actual')?.value || 0;
                    const comparison = quarterlyView === 'yoy' 
                      ? payload.find(p => p.dataKey === 'prior')?.value || 0
                      : payload.find(p => p.dataKey === 'budget')?.value || 0;
                    const volActual = payload.find(p => p.dataKey === 'volActual')?.value || 0;
                    const volComparison = quarterlyView === 'yoy'
                      ? payload.find(p => p.dataKey === 'volPrior')?.value || 0
                      : payload.find(p => p.dataKey === 'volBudget')?.value || 0;
                    const variance = ((actual - comparison) / comparison * 100).toFixed(1);
                    const compLabel = quarterlyView === 'yoy' ? `${year - 1}` : (language === 'pt' ? 'Orçamento' : 'Budget');
                    return (
                      <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700">
                        <p className="font-semibold text-gold-400 mb-2">{label}</p>
                        <div className="space-y-1 text-sm">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">{language === 'pt' ? 'Receita' : 'Revenue'}</p>
                          <div className="flex justify-between gap-4">
                            <span className="text-slate-400">{year}:</span>
                            <span className="font-semibold">{actual.toFixed(1)}M</span>
                          </div>
                          <div className="flex justify-between gap-4">
                            <span className="text-slate-400">{compLabel}:</span>
                            <span>{comparison.toFixed(1)}M</span>
                          </div>
                          <div className="border-t border-slate-600 pt-1.5 mt-1.5">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{language === 'pt' ? 'Volume' : 'Volume'}</p>
                            <div className="flex justify-between gap-4">
                              <span className="text-slate-400">{year}:</span>
                              <span className="font-semibold">{volActual} k cts</span>
                            </div>
                            <div className="flex justify-between gap-4">
                              <span className="text-slate-400">{compLabel}:</span>
                              <span>{volComparison} k cts</span>
                            </div>
                          </div>
                          <div className="border-t border-slate-600 pt-1.5 mt-1.5">
                            <span className={`text-xs font-medium ${parseFloat(variance) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {parseFloat(variance) >= 0 ? '+' : ''}{variance}% vs {compLabel}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              {/* Revenue Bars - Only show comparison based on toggle */}
              {quarterlyView === 'yoy' && (
                <Bar yAxisId="revenue" dataKey="prior" fill={colors.chart.prior} radius={[2, 2, 0, 0]} barSize={32} />
              )}
              {quarterlyView === 'budget' && (
                <Bar yAxisId="revenue" dataKey="budget" fill={colors.chart.budget} radius={[2, 2, 0, 0]} barSize={32} />
              )}
              <Bar yAxisId="revenue" dataKey="actual" fill={colors.chart.primary} radius={[2, 2, 0, 0]} barSize={32} />
              {/* Volume Lines - Only show comparison based on toggle */}
              <Line yAxisId="volume" type="monotone" dataKey="volActual" stroke={colors.chart.primary} strokeWidth={2} dot={{ r: 4, fill: colors.chart.primary, strokeWidth: 2, stroke: '#fff' }} />
              {quarterlyView === 'yoy' && (
                <Line yAxisId="volume" type="monotone" dataKey="volPrior" stroke={colors.chart.prior} strokeWidth={2} dot={{ r: 4, fill: colors.chart.prior, strokeWidth: 2, stroke: '#fff' }} />
              )}
              {quarterlyView === 'budget' && (
                <Line yAxisId="volume" type="monotone" dataKey="volBudget" stroke={colors.chart.budget} strokeWidth={2} strokeDasharray="5 3" dot={{ r: 4, fill: colors.chart.budget, strokeWidth: 2, stroke: '#fff' }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          {/* Legenda simplificada */}
          <div className="mt-4 space-y-2">
            {/* Linha 1: Barras (Receita) */}
            <div className="flex justify-center items-center gap-6 text-xs">
              <span className="text-slate-500 font-medium">{language === 'pt' ? 'Receita:' : 'Revenue:'}</span>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-3 rounded" style={{ backgroundColor: colors.chart.primary }}></div>
                <span>{year}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-3 rounded" style={{ backgroundColor: quarterlyView === 'yoy' ? colors.chart.prior : colors.chart.budget }}></div>
                <span>{quarterlyView === 'yoy' ? year - 1 : (language === 'pt' ? 'Orçamento' : 'Budget')}</span>
              </div>
            </div>
            {/* Linha 2: Linhas (Volume) */}
            <div className="flex justify-center items-center gap-6 text-xs">
              <span className="text-slate-500 font-medium">{language === 'pt' ? 'Volume:' : 'Volume:'}</span>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-0.5 rounded" style={{ backgroundColor: colors.chart.primary }}></div>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors.chart.primary }}></div>
                <span>{year}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-0.5 rounded" style={{ backgroundColor: quarterlyView === 'yoy' ? colors.chart.prior : colors.chart.budget }}></div>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: quarterlyView === 'yoy' ? colors.chart.prior : colors.chart.budget }}></div>
                <span>{quarterlyView === 'yoy' ? year - 1 : (language === 'pt' ? 'Orçamento' : 'Budget')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ BLOCK 4: REVENUE BRIDGE (Movido para aqui) ============ */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Ponte de Receita' : 'Revenue Bridge'}
          </h3>
          <div className="flex gap-2">
            <button
              onClick={() => setBridgeView('yoy')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                bridgeView === 'yoy' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              vs {year - 1}
            </button>
            <button
              onClick={() => setBridgeView('budget')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                bridgeView === 'budget' 
                  ? 'bg-primary text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              vs {language === 'pt' ? 'Orçamento' : 'Budget'}
            </button>
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={processedBridgeData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 10, fill: colors.text.secondary }} 
              stroke="transparent"
              interval={0}
              angle={-20}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fontSize: 10, fill: colors.text.tertiary }} 
              stroke="transparent"
              domain={[0, 350]}
              tickFormatter={(v) => `${v}M`}
            />
            <Tooltip content={<BridgeTooltip />} />
            <Bar dataKey="start" stackId="bridge" fill="transparent" />
            <Bar dataKey="value" stackId="bridge" radius={[3, 3, 0, 0]}>
              {processedBridgeData.map((entry, index) => {
                let fillColor = colors.chart.budget;
                if (entry.type === 'start' || entry.type === 'end') fillColor = colors.chart.primary;
                else if (entry.type === 'positive') fillColor = colors.chart.positive;
                else if (entry.type === 'negative') fillColor = colors.chart.negative;
                return <Cell key={`cell-${index}`} fill={fillColor} />;
              })}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        
        <div className="flex justify-center gap-6 mt-2 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.chart.primary }}></div>
            <span>{language === 'pt' ? 'Base/Final' : 'Base/Final'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.chart.positive }}></div>
            <span>{language === 'pt' ? 'Positivo' : 'Positive'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.chart.negative }}></div>
            <span>{language === 'pt' ? 'Negativo' : 'Negative'}</span>
          </div>
        </div>
      </div>

      {/* ============ GEOGRAPHY + CLIENT CONCENTRATION (Interactive) ============ */}
      <div className="grid grid-cols-2 gap-6">
        {/* GEOGRAPHY */}
        <div 
          className="rounded-[18px] p-6"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          <h3 
            className="text-[24px] leading-[30px] font-semibold tracking-[-0.2px] mb-5"
            style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
          >
            {language === 'pt' ? 'Receita por Geografia' : 'Revenue by Geography'}
          </h3>
          <RevenueWorldMap 
            data={revenueData.byGeography} 
            language={language}
            hoveredCountry={hoveredCountry}
            hoveredClient={hoveredClient}
            selectedCountry={selectedCountry}
            onCountryHover={setHoveredCountry}
            onCountryClick={handleCountryClick}
            isCountryHighlighted={isCountryHighlighted}
            getCountryByClient={getCountryByClient}
          />
        </div>

        {/* CLIENT CONCENTRATION */}
        <div 
          className="rounded-[18px] p-6 relative"
          style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
        >
          <div className="flex items-center justify-between mb-5">
            <h3 
              className="text-[24px] leading-[30px] font-semibold tracking-[-0.2px]"
              style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
            >
              {selectedCountry 
                ? `${language === 'pt' ? 'Concentração de Clientes' : 'Client Concentration'} – ${countryNames[selectedCountry]?.[language] || selectedCountry}`
                : (language === 'pt' ? 'Concentração de Clientes' : 'Client Concentration')
              }
            </h3>
            {selectedCountry && (
              <button
                onClick={resetSelection}
                className="px-3 py-1.5 text-[12px] font-medium rounded-[8px] transition-all duration-200 hover:bg-slate-200"
                style={{ 
                  backgroundColor: '#F1F4F9',
                  color: colors.text.secondary,
                  border: `1px solid ${colors.border.default}`
                }}
              >
                {language === 'pt' ? 'Ver total' : 'View total'}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {customerData.slice(0, 5).map((client, idx) => {
              const prevClient = prevCustomerData[idx];
              const change = client.percentage - prevClient.percentage;
              const clientCountry = getCountryByClient(client.customer);
              const isHighlighted = isClientHighlighted(client.customer);
              const isHovered = hoveredClient === client.customer;
              const isSelected = selectedCountry && getClientsByCountry(selectedCountry).includes(client.customer);
              // Highlight from country hover (not client hover) - scale 1.2
              const isCountryHoverHighlight = hoveredCountry && getClientsByCountry(hoveredCountry).includes(client.customer);
              
              return (
                <div 
                  key={idx} 
                  className={`text-center p-4 rounded-[12px] cursor-pointer transition-all duration-200 ${idx === 4 ? 'col-span-2' : ''}`}
                  style={{ 
                    backgroundColor: isSelected ? colors.status.positiveBg : isHovered ? '#EEF2F7' : '#F7F9FC',
                    opacity: (selectedCountry || hoveredCountry) && !isHighlighted ? 0.4 : 1,
                    transform: isCountryHoverHighlight ? 'scale(1.2)' : (isHovered || isSelected ? 'scale(1.05)' : 'scale(1)'),
                    border: isSelected 
                      ? `2px solid ${colors.status.positive}` 
                      : (isCountryHoverHighlight || isHovered)
                        ? `2px solid ${colors.text.tertiary}` 
                        : '2px solid transparent',
                    boxShadow: isCountryHoverHighlight 
                      ? '0 8px 24px rgba(0, 0, 0, 0.2)' 
                      : isSelected 
                        ? '0 4px 12px rgba(12, 138, 75, 0.15)' 
                        : 'none',
                    zIndex: isCountryHoverHighlight ? 10 : 1
                  }}
                  onMouseEnter={() => setHoveredClient(client.customer)}
                  onMouseLeave={() => setHoveredClient(null)}
                  onClick={() => {
                    if (clientCountry) {
                      handleCountryClick(clientCountry);
                    }
                  }}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <p className="text-[13px]" style={{ color: colors.text.tertiary }}>{client.customer}</p>
                    {clientCountry && (
                      <span title={countryNames[clientCountry]?.[language] || clientCountry}>
                        <CountryFlag countryCode={clientCountry} size={22} />
                      </span>
                    )}
                  </div>
                  <p className="text-[22px] font-semibold" style={{ color: colors.text.primary }}>{client.percentage}%</p>
                  <p 
                    className="text-[13px] font-medium"
                    style={{ color: change <= 0 ? colors.status.positive : colors.status.negative }}
                  >
                    {change > 0 ? '+' : ''}{change.toFixed(1)}p.p. YoY
                  </p>
                </div>
              );
            })}
          </div>
          
          {/* Client Monthly Chart Popup - 55% larger than country popup (357px vs 220px, +35% from 264px) */}
          {hoveredClient && clientMonthlyData[hoveredClient] && !selectedCountry && (
            <div 
              className="absolute bg-white rounded-lg shadow-xl border-2 p-4 pointer-events-none z-50 transition-all duration-300"
              style={{
                borderColor: clientColors[hoveredClient] || colors.bg.dark,
                width: '357px',
                top: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {/* Header */}
              <div className="flex items-center gap-2.5 mb-3 pb-2.5 border-b border-slate-100">
                <div 
                  className="w-3.5 h-3.5 rounded-full" 
                  style={{ backgroundColor: clientColors[hoveredClient] || colors.bg.dark }}
                />
                <span className="font-semibold text-[14px] text-slate-700">
                  {hoveredClient} - {language === 'pt' ? 'Receita Mensal' : 'Monthly Revenue'}
                </span>
                {getCountryByClient(hoveredClient) && (
                  <CountryFlag countryCode={getCountryByClient(hoveredClient)} size={18} />
                )}
              </div>
              
              {/* Bar Chart - Monthly (169px height = 125px * 1.35) */}
              <div className="h-[169px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientMonthlyData[hoveredClient]} barGap={2}>
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 9, fill: '#64748B' }} 
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis 
                      tick={{ fontSize: 9, fill: '#94A3B8' }} 
                      axisLine={false}
                      tickLine={false}
                      width={24}
                      tickFormatter={(v) => v.toFixed(1)}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1E293B',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '10px',
                        padding: '4px 8px',
                      }}
                      labelStyle={{ color: '#FFF', fontWeight: 'bold', fontSize: '10px' }}
                      itemStyle={{ color: '#FFF', fontSize: '10px' }}
                      formatter={(value) => [`$${value}M`, '']}
                    />
                    <Bar dataKey="y2024" fill="#CBD5E1" radius={[3, 3, 0, 0]} name="2024">
                      <LabelList 
                        dataKey="y2024" 
                        position="top" 
                        style={{ fontSize: '7px', fill: '#94A3B8', fontWeight: '500' }}
                        formatter={(value) => value.toFixed(1)}
                      />
                    </Bar>
                    <Bar dataKey="y2025" fill={clientColors[hoveredClient] || colors.bg.dark} radius={[3, 3, 0, 0]} name="2025">
                      <LabelList 
                        dataKey="y2025" 
                        position="top" 
                        style={{ fontSize: '7px', fill: '#0E1633', fontWeight: '600' }}
                        formatter={(value) => value.toFixed(1)}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend */}
              <div className="flex justify-center gap-4 mt-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-slate-300"></div>
                  <span className="text-slate-500 text-[11px]">2024</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div 
                    className="w-3 h-3 rounded" 
                    style={{ backgroundColor: clientColors[hoveredClient] || colors.bg.dark }}
                  />
                  <span className="text-slate-700 text-[11px] font-medium">2025</span>
                </div>
              </div>
            </div>
          )}
          
          <div 
            className="flex items-center justify-between p-5 rounded-[12px]"
            style={{ backgroundColor: '#F1F4F9' }}
          >
            <div>
              <p className="text-[14px]" style={{ color: colors.text.secondary }}>
                {selectedCountry 
                  ? `${language === 'pt' ? 'Clientes em' : 'Clients in'} ${countryNames[selectedCountry]?.[language]}`
                  : (language === 'pt' ? 'Concentração Top 5' : 'Top 5 Concentration')
                }
              </p>
              <p className="text-[28px] font-semibold" style={{ color: colors.text.primary }}>
                {selectedCountry 
                  ? `${getClientsByCountry(selectedCountry).length} ${language === 'pt' ? 'clientes' : 'clients'}`
                  : `${top5Pct}%`
                }
              </p>
            </div>
            <div className="text-right">
              <p className="text-[13px]" style={{ color: colors.text.tertiary }}>
                {selectedCountry ? '' : `2024: ${prevTop5Pct.toFixed(1)}%`}
              </p>
              {!selectedCountry && (
                <p 
                  className="text-[15px] font-medium"
                  style={{ color: top5VsPrior <= 0 ? colors.status.positive : colors.status.negative }}
                >
                  {top5VsPrior > 0 ? '+' : ''}{top5VsPrior.toFixed(1)}p.p. YoY
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ============ EXECUTIVE REVENUE TABLE ============ */}
      <div 
        className="rounded-[18px] overflow-hidden"
        style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
      >
        <div 
          className="px-6 py-5 flex items-center justify-between sticky top-0 z-10"
          style={{ borderBottom: `1px solid ${colors.border.soft}`, backgroundColor: colors.bg.card }}
        >
          <h3 
            className="text-[24px] leading-[30px] font-semibold tracking-[-0.2px]"
            style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
          >
            {language === 'pt' ? 'Tabela Executiva de Receita' : 'Executive Revenue Table'}
          </h3>
          {/* Toggle: Vista por Métrica / Vista por Tipo de Pedra */}
          <div className="flex gap-2">
            <button
              onClick={() => setTableView('metric')}
              className="px-4 py-2 text-[14px] font-medium transition-colors"
              style={{ 
                backgroundColor: tableView === 'metric' ? colors.bg.dark : '#F1F4F9',
                color: tableView === 'metric' ? '#FFFFFF' : colors.text.secondary,
                borderRadius: '10px',
                border: tableView === 'metric' ? 'none' : `1px solid ${colors.border.default}`
              }}
            >
              {language === 'pt' ? 'Vista por Métrica' : 'View by Metric'}
            </button>
            <button
              onClick={() => setTableView('stonetype')}
              className="px-4 py-2 text-[14px] font-medium transition-colors"
              style={{ 
                backgroundColor: tableView === 'stonetype' ? colors.bg.dark : '#F1F4F9',
                color: tableView === 'stonetype' ? '#FFFFFF' : colors.text.secondary,
                borderRadius: '10px',
                border: tableView === 'stonetype' ? 'none' : `1px solid ${colors.border.default}`
              }}
            >
              {language === 'pt' ? 'Vista por Tipo de Pedra' : 'View by Stone Type'}
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {tableView === 'metric' ? (
            /* ===== VISTA POR MÉTRICA (Original) ===== */
            <table className="w-full text-[15px]">
              <thead className="sticky top-0 z-10">
                <tr style={{ backgroundColor: colors.bg.dark }}>
                  <th className="text-left py-4 px-4 text-[13px] font-semibold text-white tracking-wider w-[24%]">
                    {language === 'pt' ? 'Métrica' : 'Metric'}
                  </th>
                  <th className="text-right py-4 px-3 text-[13px] font-semibold text-white tracking-wider w-[12%]">
                    {year}
                  </th>
                  <th className="text-right py-4 px-3 text-[13px] font-semibold text-white tracking-wider w-[12%]">
                    {language === 'pt' ? 'Orçamento' : 'Budget'} {year}
                  </th>
                  <th className="text-right py-4 px-3 text-[13px] font-semibold tracking-wider w-[12%]" style={{ color: colors.accent.gold, backgroundColor: colors.bg.dark }}>
                    {language === 'pt' ? 'Var. Abs.' : 'Abs. Var.'}
                  </th>
                  <th className="text-right py-4 px-3 text-[13px] font-semibold tracking-wider w-[12%]" style={{ color: colors.accent.gold, backgroundColor: colors.bg.dark }}>
                    {language === 'pt' ? 'Var. %' : 'Var. %'}
                  </th>
                  <th className="text-right py-4 px-3 text-[13px] font-semibold text-slate-400 tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.dark }}>
                    {year - 1}
                  </th>
                  <th className="text-right py-4 px-3 text-[13px] font-semibold text-slate-400 tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.dark }}>
                    {language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {revenueTableData.map((row, idx) => {
                  const absVar = row.isPercent 
                    ? (row.actual - row.budget).toFixed(1)
                    : (row.actual - row.budget).toFixed(1);
                  const pctVar = row.budget !== 0 && !row.isPercent
                    ? ((row.actual - row.budget) / row.budget * 100).toFixed(1)
                    : row.isPercent 
                      ? (row.actual - row.budget).toFixed(1)
                      : '0.0';
                  const pctVarYoY = row.prior !== 0 && !row.isPercent
                    ? ((row.actual - row.prior) / row.prior * 100).toFixed(1)
                    : row.isPercent 
                      ? (row.actual - row.prior).toFixed(1)
                      : '0.0';
                  
                  const isHeader = row.type === 'header';
                  const varIsGood = row.inverse 
                    ? parseFloat(pctVar) <= 0 
                    : parseFloat(pctVar) >= 0;
                  const yoyIsGood = row.inverse 
                    ? parseFloat(pctVarYoY) <= 0 
                    : parseFloat(pctVarYoY) >= 0;
                  
                  // Header row (group label)
                  if (isHeader) {
                    return (
                      <tr key={idx} style={{ backgroundColor: '#F7F9FC', borderBottom: `1px solid ${colors.border.default}` }}>
                        <td className="py-3 px-4 font-bold text-primary">{row.label[language]}</td>
                        <td className="py-3 px-3 text-right font-bold tabular-nums text-primary">
                          {row.isPercent ? `${row.actual.toFixed(1)}%` : formatM(row.actual)}
                        </td>
                        <td className="py-3 px-3 text-right font-semibold tabular-nums text-slate-600">
                          {row.isPercent ? `${row.budget.toFixed(1)}%` : formatM(row.budget)}
                        </td>
                        <td className="py-3 px-3 text-right tabular-nums">
                          <span className="font-semibold" style={{ color: varIsGood ? colors.status.positive : colors.status.negative }}>
                            {parseFloat(absVar) > 0 ? '+' : ''}{absVar}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span 
                            className="inline-flex px-1.5 py-0.5 rounded text-xs font-bold"
                            style={{ 
                              backgroundColor: varIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                              color: varIsGood ? colors.status.positive : colors.status.negative
                            }}
                          >
                            {parseFloat(pctVar) > 0 ? '+' : ''}{pctVar}%
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right font-semibold tabular-nums text-slate-500">
                          {row.isPercent ? `${row.prior.toFixed(1)}%` : formatM(row.prior)}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <span 
                            className="inline-flex px-1.5 py-0.5 rounded text-xs font-bold"
                            style={{ 
                              backgroundColor: yoyIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                              color: yoyIsGood ? colors.status.positive : colors.status.negative
                            }}
                          >
                            {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY}%
                          </span>
                        </td>
                      </tr>
                    );
                  }
                  
                  // Regular row (sub-item)
                  return (
                    <tr 
                      key={idx} 
                      className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors"
                    >
                      <td className="py-2.5 px-4 pl-8 text-slate-600">{row.label[language]}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {row.isPercent ? `${row.actual.toFixed(1)}%` : formatM(row.actual)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                        {row.isPercent ? `${row.budget.toFixed(1)}%` : formatM(row.budget)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        <span style={{ color: varIsGood ? colors.status.positive : colors.status.negative }}>
                          {parseFloat(absVar) > 0 ? '+' : ''}{absVar}{row.isPercent ? ' p.p.' : ''}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span 
                          className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold"
                          style={{ 
                            backgroundColor: varIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                            color: varIsGood ? colors.status.positive : colors.status.negative
                          }}
                        >
                          {parseFloat(pctVar) > 0 ? '+' : ''}{pctVar}{row.isPercent ? ' p.p.' : '%'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-slate-400">
                        {row.isPercent ? `${row.prior.toFixed(1)}%` : formatM(row.prior)}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span 
                          className="text-xs font-medium"
                          style={{ color: yoyIsGood ? colors.status.positive : colors.status.negative }}
                        >
                          {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY}{row.isPercent ? ' p.p.' : '%'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            /* ===== VISTA POR TIPO DE PEDRA ===== */
            (() => {
              // Dados organizados por tipo de pedra
              const stoneTypeData = [
                // PEDRAS REGULARES
                {
                  type: 'header',
                  stoneType: { pt: 'Pedras Regulares', en: 'Regular Stones' },
                  color: '#334155' // slate-700 - diferente do header da tabela
                },
                {
                  type: 'row',
                  label: { pt: 'Receita', en: 'Revenue' },
                  actual: revenueData.byType[0].value,
                  budget: budgetRevenue * 0.73,
                  prior: prevRevenueData.byType[0].value,
                  unit: 'M USD'
                },
                {
                  type: 'row',
                  label: { pt: 'Volume', en: 'Volume' },
                  actual: (revenueData.byType[0].volume || totalCarats * 0.75) / 1000,
                  budget: budgetVolume * 0.75,
                  prior: (prevRevenueData.byType[0].volume || prevTotalCarats * 0.75) / 1000,
                  unit: 'k cts'
                },
                {
                  type: 'row',
                  label: { pt: 'Preço Médio', en: 'Avg Price' },
                  actual: revenueData.avgPriceByType?.regular || 237,
                  budget: 229,
                  prior: prevRevenueData.avgPriceByType?.regular || 215,
                  unit: 'USD/ct'
                },
                // PEDRAS ESPECIAIS
                {
                  type: 'header',
                  stoneType: { pt: 'Pedras Especiais', en: 'Special Stones' },
                  color: colors.accent.gold
                },
                {
                  type: 'row',
                  label: { pt: 'Receita', en: 'Revenue' },
                  actual: revenueData.byType[1].value,
                  budget: budgetRevenue * 0.20,
                  prior: prevRevenueData.byType[1].value,
                  unit: 'M USD'
                },
                {
                  type: 'row',
                  label: { pt: 'Volume', en: 'Volume' },
                  actual: (revenueData.byType[1].volume || totalCarats * 0.15) / 1000,
                  budget: budgetVolume * 0.15,
                  prior: (prevRevenueData.byType[1].volume || prevTotalCarats * 0.15) / 1000,
                  unit: 'k cts'
                },
                {
                  type: 'row',
                  label: { pt: 'Preço Médio', en: 'Avg Price' },
                  actual: revenueData.avgPriceByType?.special || 520,
                  budget: 494,
                  prior: prevRevenueData.avgPriceByType?.special || 453,
                  unit: 'USD/ct'
                },
                // REJEITADOS
                {
                  type: 'header',
                  stoneType: { pt: 'Rejeitados', en: 'Rejected' },
                  color: colors.text.tertiary
                },
                {
                  type: 'row',
                  label: { pt: 'Receita', en: 'Revenue' },
                  actual: revenueData.byType[2].value,
                  budget: budgetRevenue * 0.07,
                  prior: prevRevenueData.byType[2].value,
                  unit: 'M USD'
                },
                {
                  type: 'row',
                  label: { pt: 'Volume', en: 'Volume' },
                  actual: (revenueData.byType[2].volume || totalCarats * 0.10) / 1000,
                  budget: budgetVolume * 0.10,
                  prior: (prevRevenueData.byType[2].volume || prevTotalCarats * 0.10) / 1000,
                  unit: 'k cts'
                },
                {
                  type: 'row',
                  label: { pt: 'Preço Médio', en: 'Avg Price' },
                  actual: revenueData.avgPriceByType?.lowGrade || 120,
                  budget: 115,
                  prior: prevRevenueData.avgPriceByType?.lowGrade || 110,
                  unit: 'USD/ct'
                },
                // TOTAL GERAL
                {
                  type: 'total',
                  label: { pt: 'Receita Total', en: 'Total Revenue' },
                  actual: yearlyData.revenue,
                  budget: budgetRevenue,
                  prior: prevYearData.revenue,
                  unit: 'M USD'
                }
              ];

              return (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr style={{ backgroundColor: colors.bg.header }}>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-white uppercase tracking-wider w-[24%]" style={{ backgroundColor: colors.bg.header }}>
                        {language === 'pt' ? 'Tipo de Pedra / Métrica' : 'Stone Type / Metric'}
                      </th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-white uppercase tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.header }}>
                        {year}
                      </th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-white uppercase tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.header }}>
                        {language === 'pt' ? 'Orçamento' : 'Budget'} {year}
                      </th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.header }}>
                        {language === 'pt' ? 'Var. Abs.' : 'Abs. Var.'}
                      </th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.header }}>
                        {language === 'pt' ? 'Var. %' : 'Var. %'}
                      </th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.header }}>
                        {year - 1}
                      </th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[12%]" style={{ backgroundColor: colors.bg.header }}>
                        {language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stoneTypeData.map((row, idx) => {
                      // Header row (stone type)
                      if (row.type === 'header') {
                        return (
                          <tr key={idx} style={{ backgroundColor: row.color }}>
                            <td colSpan={7} className="py-2.5 px-4 font-bold text-white text-sm tracking-wide">
                              {row.stoneType[language]}
                            </td>
                          </tr>
                        );
                      }
                      
                      // Total row
                      if (row.type === 'total') {
                        const absVar = (row.actual - row.budget).toFixed(1);
                        const pctVar = ((row.actual - row.budget) / row.budget * 100).toFixed(1);
                        const pctVarYoY = ((row.actual - row.prior) / row.prior * 100).toFixed(1);
                        const varIsGood = parseFloat(pctVar) >= 0;
                        const yoyIsGood = parseFloat(pctVarYoY) >= 0;
                        
                        return (
                          <tr key={idx} className="bg-slate-100 border-t-2 border-slate-300">
                            <td className="py-3 px-4 font-bold text-primary">{row.label[language]}</td>
                            <td className="py-3 px-3 text-right font-bold tabular-nums text-primary">
                              {formatM(row.actual)}
                            </td>
                            <td className="py-3 px-3 text-right font-semibold tabular-nums text-slate-600">
                              {formatM(row.budget)}
                            </td>
                            <td className="py-3 px-3 text-right tabular-nums">
                              <span className="font-semibold" style={{ color: varIsGood ? colors.status.positive : colors.status.negative }}>
                                {parseFloat(absVar) > 0 ? '+' : ''}{absVar}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-right">
                              <span 
                                className="inline-flex px-1.5 py-0.5 rounded text-xs font-bold"
                                style={{ 
                                  backgroundColor: varIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                                  color: varIsGood ? colors.status.positive : colors.status.negative
                                }}
                              >
                                {parseFloat(pctVar) > 0 ? '+' : ''}{pctVar}%
                              </span>
                            </td>
                            <td className="py-3 px-3 text-right font-semibold tabular-nums text-slate-500">
                              {formatM(row.prior)}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <span 
                                className="inline-flex px-1.5 py-0.5 rounded text-xs font-bold"
                                style={{ 
                                  backgroundColor: yoyIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                                  color: yoyIsGood ? colors.status.positive : colors.status.negative
                                }}
                              >
                                {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY}%
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      
                      // Regular data row
                      const absVar = (row.actual - row.budget).toFixed(1);
                      const pctVar = row.budget !== 0 
                        ? ((row.actual - row.budget) / row.budget * 100).toFixed(1)
                        : '0.0';
                      const pctVarYoY = row.prior !== 0 
                        ? ((row.actual - row.prior) / row.prior * 100).toFixed(1)
                        : '0.0';
                      const varIsGood = parseFloat(pctVar) >= 0;
                      const yoyIsGood = parseFloat(pctVarYoY) >= 0;
                      
                      return (
                        <tr 
                          key={idx} 
                          className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors"
                        >
                          <td className="py-2.5 px-4 pl-6 text-slate-600">
                            {row.label[language]}
                            <span className="text-slate-400 text-xs ml-1">({row.unit})</span>
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums">
                            {formatM(row.actual)}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-slate-500">
                            {formatM(row.budget)}
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums">
                            <span style={{ color: varIsGood ? colors.status.positive : colors.status.negative }}>
                              {parseFloat(absVar) > 0 ? '+' : ''}{absVar}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <span 
                              className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold"
                              style={{ 
                                backgroundColor: varIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                                color: varIsGood ? colors.status.positive : colors.status.negative
                              }}
                            >
                              {parseFloat(pctVar) > 0 ? '+' : ''}{pctVar}%
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-slate-400">
                            {formatM(row.prior)}
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <span 
                              className="text-xs font-medium"
                              style={{ color: yoyIsGood ? colors.status.positive : colors.status.negative }}
                            >
                              {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalysis;
